import { useCallback, useEffect, useState } from 'react';
import { Form as ReactFinalForm } from 'react-final-form';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { replace } from 'connected-react-router';
import type { MessageTranslator } from '@oneflowab/pomes';
import type { match as Match } from 'react-router';

// eslint-disable-next-line import/named
import { hasAnyPosition } from '@flow/libs/oneflow-client/positions';
import positionsReducer from '@app/reducers/entities/positions';
import accountsReducer from '@app/reducers/entities/accounts';
import accountSeatsReducer from '@app/reducers/entities/account-seats';
import { hasAvailableSeats } from '@flow/libs/account';
import { getAccountFromSessionSelector, isFeatureEnabledSelector } from '@app/reducers/session';

import adminPage from '@app/hocs/admin-page';
import Field from '@app/components/field';
import * as EditableForm from '@app/components/editable-form';
import { DetailsFooter } from '@app/components/details-footer';
import { LocalizedDateTime } from '@app/components/localized-date-time';
import { checkAcl } from '@flow/libs/utils/acl';
import TextField from '@app/components/text-field';
import Toggle from '@app/components/toggle';
import SelectField from '@app/components/select-field';
import UserRole, { getUserRolesAsOptions, getUserRole } from '@app/components/user-roles';
import emailValidationText from '@app/components/email-validation-text';
import { T } from '@app/components/translation';
import UserSecurityStatus from '@app/components/user-security-status';

import { USER_ROLE_USER, USER_ROLE_LIMITED, isUserActive } from '@flow/libs/user';

import EmailChangeNotification from '@app/components/details/notification/email-change-notification';
import ChangePending from '@app/components/details/status-pill/change-pending';
import style from './user-details.module.scss';

type UpdateUserData = {
  id: number;
  data: Oneflow.Position;
};

export type Props = {
  message: MessageTranslator;
  location: Location;
  match: Match;
};

export function UserDetails({ message, location, match }: Props) {
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const position = useSelector((state) =>
    positionsReducer.getPositionSelector(state, {
      id: match.params.id,
    }),
  );
  const account: Oneflow.Account = useSelector((state) => getAccountFromSessionSelector(state));
  const accountSeats = useSelector((state) =>
    accountSeatsReducer.getAccountSeatSelector(state, {
      id: account.id,
    }),
  );
  const availableSeats = hasAvailableSeats(accountSeats);
  // Until we have proper ACLs or similar for this, we use enabledExtensions
  // remove feature flag when it's all migrated
  const isPositionLimitedFeatureEnabled = useSelector((state) =>
    isFeatureEnabledSelector(state, {
      feature: 'temporaryPositionLimited',
    }),
  );
  const isPositionLimitedExtensionEnabled = account.enabledExtensions?.some(
    (extension) => extension.key === 'limited_users',
  );
  const isPositionLimitedEnabled =
    isPositionLimitedFeatureEnabled || isPositionLimitedExtensionEnabled;
  const updateState = useSelector((state) =>
    positionsReducer.getUpdateSelector(state, {
      id: position.id,
    }),
  );

  const resetUpdateState = useCallback(() => {
    dispatch(
      positionsReducer.updatePositionReset({
        id: match.params.id,
      }),
    );
  }, [
    dispatch,
    match.params.id,
  ]);

  const updateUser = (data: UpdateUserData) => {
    dispatch(
      positionsReducer.updatePosition({
        id: match.params.id,
        data: {
          title: null,
          phoneNumber: null,
          fullname: null,
          ...data,
          userRole: get(data, 'userRole.value'),
        },
        pipe: {
          onSuccess: () => {
            setIsEditMode(false);
          },
        },
      }),
    );
  };

  const uniquePositionEmail = ({ params }: { params: any }) =>
    hasAnyPosition({
      params,
      entityId: position.id,
      props: {
        returnEntity: true,
      },
    });

  const checkEditPermission = useCallback(
    () =>
      checkAcl(
        position.acl,
        [
          'position:update:active',
          'position:update:fullname',
          'position:update:email',
          'position:update:phone_number',
          'position:update:title',
        ],
        {
          match: 'any',
        },
      ),
    [
      position.acl,
    ],
  );

  const hasError = () => updateState.error;

  const handleEditMode = useCallback(
    (newEditMode: boolean) => {
      resetUpdateState();
      setIsEditMode(newEditMode);
    },
    [
      resetUpdateState,
    ],
  );

  const enableEditModeIfRedirected = useCallback(() => {
    if (!isEditMode && location?.state?.edit && checkEditPermission()) {
      dispatch(replace(location.pathname));
      setIsEditMode(true);
    }
  }, [
    location?.state?.edit,
    isEditMode,
    checkEditPermission,
    location.pathname,
    dispatch,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: The effect needs to be memoized
  useEffect(() => {
    dispatch(
      accountsReducer.fetchAccount({
        id: account.id,
      }),
    );
  }, []);

  useEffect(() => {
    enableEditModeIfRedirected();
  }, [
    enableEditModeIfRedirected,
  ]);

  const getAvailableUserRoles = () => {
    const isUserRoleLimited = position.userRole === USER_ROLE_LIMITED;

    if (isUserRoleLimited && !availableSeats && isPositionLimitedEnabled) {
      return [
        getUserRole(USER_ROLE_LIMITED, isPositionLimitedEnabled, message),
      ];
    }

    return getUserRolesAsOptions(isPositionLimitedEnabled, message);
  };

  const getInitialValues = () => {
    const { fullname, email, phoneNumber, title, userRole: positionUserRole } = position;

    const isRoleUser = positionUserRole === USER_ROLE_USER;
    const userRole = isRoleUser ? USER_ROLE_USER : USER_ROLE_LIMITED;

    return {
      active: isUserActive(position),
      fullname,
      phoneNumber,
      title,
      email,
      userRole,
    };
  };

  const getErrorMessage = () => {
    if (updateState.error && updateState.error.body?.status_code === 409) {
      return (
        <T
          _str="You cannot deactivate this user or change the user type. At least one active admin must remain."
          _comment="Used to show the error message if the last administrator user wanted to change"
        />
      );
    }
    return null;
  };

  const checkChangedEmail = (newEmail: string) => {
    const emailChanged = position.email !== newEmail;

    if (newEmail && emailChanged) {
      setPendingEmail(newEmail);
      setIsEditMode(false);
    }
  };

  const infoPairs = [
    {
      label: (
        <T
          _str="ID"
          _comment="ID label for the details footer"
        />
      ),
      value: position.id,
    },
    {
      label: (
        <T
          _str="Created"
          _comment="Created date label for the details footer"
        />
      ),
      value: <LocalizedDateTime datetime={position.createdTime} />,
    },
    {
      label: (
        <T
          _str="Two-step authentication"
          _comment="Two-step authentication label for the details footer"
        />
      ),
      value: (<UserSecurityStatus position={position} />) as number,
    },
  ];

  return (
    <ReactFinalForm
      initialValues={getInitialValues()}
      onSubmit={updateUser}
      render={({ handleSubmit, ...formProps }) => (
        <form
          onSubmit={(values) => {
            handleSubmit(values);
            checkChangedEmail(formProps.values.email);
            formProps.form.change('email', position.email);
          }}
        >
          <EditableForm.Header>
            <EditableForm.Actions
              resetFormState={resetUpdateState}
              formState={updateState}
              isEditMode={isEditMode}
              setIsEditMode={handleEditMode}
              disabled={!checkEditPermission()}
            />
            {hasError() && <EditableForm.ErrorMessage errorMessage={getErrorMessage()} />}
          </EditableForm.Header>
          <EditableForm.Body>
            <div className={style.FormBody}>
              <Field
                id="fullname"
                name="fullname"
                component={TextField}
                value={position.fullname}
                placeholder={message({
                  id: 'Enter full name',
                  comment: 'Placeholder for the name field on the user details page.',
                })}
                autoFocus
                maxLength={100}
                required
                label={message({
                  id: 'Full name',
                  comment: 'Label for the name field on the user details page.',
                })}
                disabled={!isEditMode || !checkAcl(position.acl, 'position:update:fullname')}
                labelCustomClass={style.Label}
              />
              <div className={style.EmailContainer}>
                <Field
                  id="email"
                  name="email"
                  component={TextField}
                  value={position.email}
                  placeholder={message({
                    id: 'Enter email',
                    comment:
                      'Used as the placeholder of the relevant field in the details form of the profile page',
                  })}
                  maxLength={70}
                  unique={{
                    text: emailValidationText,
                    param: 'email',
                    request: uniquePositionEmail,
                    initialValue: position.email,
                  }}
                  email
                  required
                  label={message({
                    id: 'Email address',
                    comment:
                      'Used as the label of the relevant field in the details form of the profile page',
                  })}
                  disabled={!isEditMode || !checkAcl(position.acl, 'position:update:email')}
                  labelCustomClass={style.Label}
                />
                {pendingEmail && <ChangePending />}
              </div>
              {pendingEmail && <EmailChangeNotification email={pendingEmail} />}
              <Field
                name="title"
                component={TextField}
                value={position.title || '-'}
                placeholder={message({
                  id: 'Enter title',
                  comment: 'Placeholder for the title field on the user details page.',
                })}
                maxLength={100}
                label={message({
                  id: 'Title',
                  comment: 'Label for the title field on the user details page.',
                })}
                disabled={!isEditMode || !checkAcl(position.acl, 'position:update:title')}
                labelCustomClass={style.Label}
              />
              <Field
                name="phoneNumber"
                component={TextField}
                value={position.phoneNumber || '-'}
                placeholder={message({
                  id: 'Enter phone number',
                  comment: 'Placeholder for the phone number field on the user details page.',
                })}
                maxLength={50}
                disabled={!isEditMode || !checkAcl(position.acl, 'position:update:phone_number')}
                label={message({
                  id: 'Phone number',
                  comment: 'Label for the phone number field on the user details page.',
                })}
                labelCustomClass={style.Label}
              />
              <Field
                name="userRole"
                component={SelectField}
                options={getAvailableUserRoles()}
                value={
                  <UserRole
                    userRole={position.userRole}
                    withTooltip
                  />
                }
                clearable={false}
                searchable={false}
                placeholder={message({
                  id: 'Select user type',
                  comment: 'Placeholder for the type field on the user details page.',
                })}
                required
                label={message({
                  id: 'User type',
                  comment: 'Label for the type field on the user details page.',
                })}
                disabled={!isEditMode || !checkAcl(position.acl, 'position:update:user_role')}
                labelCustomClass={style.Label}
              />
              <EditableForm.FormItem className={style.Flex}>
                <div className={style.LabelContainer}>
                  <label
                    className={style.Label}
                    htmlFor="active"
                  >
                    <T
                      _str="Status"
                      _comment="Label for the status field on the user details page."
                    />
                  </label>
                </div>
                <Field
                  name="active"
                  component={Toggle}
                  version="latest"
                  disabled={
                    !isEditMode ||
                    !availableSeats ||
                    !checkAcl(position.acl, 'position:update:active')
                  }
                  containerClassName={style.Label}
                />
              </EditableForm.FormItem>
            </div>
          </EditableForm.Body>
          <DetailsFooter infoPairs={infoPairs} />
        </form>
      )}
    />
  );
}

export default adminPage(({ props: { message } }) => ({
  title: message({
    id: 'Details',
    comment: 'The page title',
  }),
  modules: [
    [],
  ],
}))(UserDetails);
